import {Injectable} from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor
} from '@angular/common/http';
import {catchError, from, Observable, switchMap, throwError, of} from 'rxjs';
import {AuthService} from "../../services/auth.service";
import {environment} from "../../../environments/environment";

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private readonly isTokenAuth = environment.isTokenAuth;

  constructor(private authService: AuthService) {
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Skip auth header for login or logout requests
    if (this.isAuthenticationRequest(request.url)) {
      return next.handle(request);
    }

    return from(this.authService.getAccesstokenOrRefreshtokenOrApikey(this.isTokenAuth ? 'accessToken' : 'api-key')).pipe(
      switchMap((authToken) => {
        const clonedRequest = authToken
          ? request.clone({setHeaders: {Authorization: `${this.isTokenAuth ? 'JWT' : ''} ${authToken}`}})
          : request.clone();

        return next.handle(clonedRequest).pipe(
          catchError((err) => {
            if (err.status === 401 && authToken) {
              console.warn('🔁 Access token expired. Trying to refresh...');

              return this.authService.refreshToken().pipe(
                switchMap((loginResult) => {
                  const newAuthToken = loginResult.accessToken;

                  if (!newAuthToken) {
                    this.authService.logout().then();
                    return throwError(() => new Error('No new token after refresh'));
                  }

                  const retryRequest = request.clone({
                    setHeaders: {Authorization: `JWT ${newAuthToken}`}
                  });

                  return next.handle(retryRequest);
                }),
                catchError((refreshError) => {
                  console.error('Token refresh failed. Logging out...', refreshError);
                  this.authService.logout().then();
                  return throwError(() => refreshError);
                })
              );
            }

            return throwError(() => err);
          })
        );
      })
    );
  }

  private isAuthenticationRequest(url: string): boolean {
    return url.includes('/auth') || url.includes('/logout');
  }
}
